import { capitalize, get } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import { IdentificationDocuments } from '@/modules/entity/components/common/IdentificationDocuments.tsx';
import { PersonIdentificationDocuments } from '@/modules/entity/components/common/PersonIdentificationDocuments.tsx';
import { PersonIndividualDetails } from '@/modules/entity/components/common/PersonIndividualDetails.tsx';
import { CompanyDetails } from '@/modules/entity/components/company/CompanyDetails.tsx';
import { useEntityItemUncachedQuery } from '@/modules/entity/queries/entity.tsx';
import { ShareholderDetails } from '@/modules/entity/types/EntityItemResponse.ts';
import { OptionalStepsProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { Alert } from 'ui';
import { AlertBlueIcon } from 'ui/icons';

export interface PersonBaseDetails extends ShareholderDetails {
  num: number;
  typeStakeholder: string;
}

const individualTrusteeRoad = [PersonIndividualDetails, PersonIdentificationDocuments];
const companyTrusteeRoad = [CompanyDetails, IdentificationDocuments];

export const MappedTrustee = ({
  moveForward,
  field_name,
  entity_type_prefix,
  person_details_key,
  label,
  allowSkip,
  navName,
}: OptionalStepsProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));
  const [params, setParams] = useSearchParams();

  const step = params.get('step') ?? '0';

  const getSubStep = useCallback((step: number) => +(params.get(`sub_step${step}`) ?? 0), [params]);
  const getSubStepRoad = useCallback(
    (step: number, subStep: number) => +(params.get(`sub_step_road${step}_${subStep}`) ?? 0),
    [params],
  );
  const setSubStep = useCallback(
    (step: number, value: number) => params.set(`sub_step${step}`, String(value)),
    [params],
  );
  const setSubStepRoad = useCallback(
    (step: number, subStep: number, value: number) =>
      params.set(`sub_step_road${step}_${subStep}`, String(value)),
    [params],
  );
  const delSubStepRoad = useCallback(
    (step: number, subStep: number) => params.delete(`sub_step_road${step}_${subStep}`),
    [params],
  );

  const subStep = getSubStep(+step);
  const subStepRoad = getSubStepRoad(+step, subStep);
  const [currentSubStep, setCurrentSubStep] = useState(subStep && !isNaN(+subStep) ? +subStep : 0); // trustee
  const [currentSubStepRoad, setCurrentSubStepRoad] = useState(
    subStepRoad && !isNaN(+subStepRoad) ? +subStepRoad : 0,
  );

  const entityTypeDetailsShareholders = useMemo(() => {
    const entityTypeDetails = get(data?.entity, entity_type_prefix);
    return (entityTypeDetails?.[field_name] as ShareholderDetails[] | undefined)?.filter(
      item => !item.same_as_index,
    );
  }, [data?.entity, entity_type_prefix, field_name]);

  useEffect(() => {
    setCurrentSubStep(subStep && !isNaN(+subStep) ? +subStep : 0);
    setCurrentSubStepRoad(subStepRoad && !isNaN(+subStepRoad) ? +subStepRoad : 0);
  }, [subStep, subStepRoad]);

  const moveToNextEntity = () => {
    const newSubStep = currentSubStep + 1;
    if (entityTypeDetailsShareholders?.[newSubStep]) {
      setSubStep(+step, newSubStep);
      setCurrentSubStep(newSubStep);
      setSubStepRoad(+step, newSubStep, 0);
      delSubStepRoad(+step, currentSubStep);
      setCurrentSubStepRoad(0);
      setParams(params, { replace: false });
    }

    if (!entityTypeDetailsShareholders?.[newSubStep]) {
      moveForward && moveForward();
    }
  };

  const moveToNextSubroadPage = () => {
    const newSubRoad = currentSubStepRoad + 1;
    setSubStepRoad(+step, currentSubStep, newSubRoad);
    setCurrentSubStepRoad(newSubRoad);
    setParams(params, { replace: false });
  };

  const findStakeholdersByIndex = useCallback(
    (index: string): PersonBaseDetails => {
      const entityTypeDetails = get(data?.entity, entity_type_prefix);
      const trustee: PersonBaseDetails[] = (
        (entityTypeDetails?.trustee as PersonBaseDetails[]) || []
      ).map((item, i): PersonBaseDetails => ({ ...item, num: i + 1, typeStakeholder: 'Trustee' }));
      const beneficiaries: PersonBaseDetails[] = (
        (entityTypeDetails?.beneficiaries as PersonBaseDetails[]) || []
      ).map((item, i) => ({ ...item, num: i + 1, typeStakeholder: 'Beneficiary' }));
      const settlors: PersonBaseDetails[] = (
        (entityTypeDetails?.settlors as PersonBaseDetails[]) || []
      ).map((item, i) => ({ ...item, num: i + 1, typeStakeholder: 'Settlor' }));
      const shareholders: PersonBaseDetails[] = (
        (entityTypeDetails?.shareholders as PersonBaseDetails[]) || []
      ).map((item, i) => ({ ...item, num: i + 1, typeStakeholder: 'Shareholder' }));
      const directors: PersonBaseDetails[] = (
        (entityTypeDetails?.directors as PersonBaseDetails[]) || []
      ).map((item, i) => ({ ...item, num: i + 1, typeStakeholder: 'Director' }));
      const officers: PersonBaseDetails[] = (
        (entityTypeDetails?.officers as PersonBaseDetails[]) || []
      ).map(
        (item, i): PersonBaseDetails => ({
          ...item,
          num: i + 1,
          typeStakeholder: 'Public Officer',
        }),
      );
      const stakeholders: PersonBaseDetails[] = (
        (entityTypeDetails?.stakeholders as PersonBaseDetails[]) || []
      ).map(
        (item, i): PersonBaseDetails => ({ ...item, num: i + 1, typeStakeholder: 'Stakeholder' }),
      );

      return [
        ...trustee,
        ...beneficiaries,
        ...settlors,
        ...shareholders,
        ...directors,
        ...officers,
        ...stakeholders,
      ].find(item => item.index === index) as PersonBaseDetails;
    },
    [data?.entity, entity_type_prefix],
  );

  const realIndex = useMemo(() => {
    const entityTypeDetails = get(data?.entity, entity_type_prefix);

    return (
      (entityTypeDetails?.[field_name] as ShareholderDetails[] | undefined)?.findIndex(
        item => item.index === entityTypeDetailsShareholders?.[currentSubStep]?.index,
      ) ?? 0
    );
  }, [currentSubStep, data?.entity, entityTypeDetailsShareholders, entity_type_prefix, field_name]);

  const title = useMemo(() => {
    return currentSubStepRoad === 1 &&
      entityTypeDetailsShareholders?.[currentSubStep]?.type === 'company'
      ? `${capitalize(
          person_details_key?.endsWith('s') ? person_details_key?.slice(0, -1) : person_details_key,
        )} #${realIndex + 1} ${
          entityTypeDetailsShareholders?.[currentSubStep]?.company_name ?? ''
        } Company extract`
      : currentSubStepRoad === 0
      ? `Tell us about ${capitalize(
          person_details_key?.endsWith('s') ? person_details_key?.slice(0, -1) : person_details_key,
        )} #${realIndex + 1} ${
          entityTypeDetailsShareholders?.[currentSubStep]?.type === 'company'
            ? 'Company details'
            : ''
        }`
      : undefined;
  }, [
    currentSubStep,
    currentSubStepRoad,
    entityTypeDetailsShareholders,
    person_details_key,
    realIndex,
  ]);

  if (entityTypeDetailsShareholders?.[currentSubStep]) {
    if (entityTypeDetailsShareholders?.[currentSubStep]?.same_as_index) {
      const sameAsStakeholder = findStakeholdersByIndex(
        entityTypeDetailsShareholders?.[currentSubStep]?.same_as_index ?? '',
      );
      return (
        <OnboardingWrapper
          title={`${capitalize(person_details_key)} #${currentSubStep + 1}`}
          onClickSkip={allowSkip ? moveToNextEntity : undefined}
          isLoading={false}
        >
          <Alert variant="neutral-2" icon={AlertBlueIcon}>
            <>
              This {capitalize(field_name)} same as a {sameAsStakeholder?.typeStakeholder} #
              {sameAsStakeholder?.num}
              <br /> if you want to change the data then change it
              <br /> Please skip this step.
            </>
          </Alert>
        </OnboardingWrapper>
      );
    }

    const CurrentStep =
      !!entityTypeDetailsShareholders?.[currentSubStep]?.company_name ||
      entityTypeDetailsShareholders?.[currentSubStep]?.type === 'company'
        ? companyTrusteeRoad[currentSubStepRoad]
        : individualTrusteeRoad[currentSubStepRoad];

    return (
      <CurrentStep
        titleHint={navName}
        key={`${entity_type_prefix}_${person_details_key}_step_${currentSubStep}_subroadStep_${currentSubStepRoad}_${title}`}
        person_details_key={person_details_key}
        entity_type_prefix={entity_type_prefix}
        item_entity_type={entityTypeDetailsShareholders?.[currentSubStep]?.type}
        label={currentSubStepRoad === 1 ? label : undefined}
        title={title}
        index={realIndex}
        uuidIndex={entityTypeDetailsShareholders?.[currentSubStep]?.index}
        moveForward={currentSubStepRoad === 1 ? moveToNextEntity : moveToNextSubroadPage}
        allowSkip={allowSkip}
      />
    );
  }

  return (
    <OnboardingWrapper
      title={capitalize(field_name)}
      onClickSkip={allowSkip ? moveForward : undefined}
      isLoading={false}
    >
      <Alert variant="neutral-2" icon={AlertBlueIcon}>
        <>{capitalize(field_name)} are not added, please skip this step.</>
      </Alert>
    </OnboardingWrapper>
  );
};
